import { RefObject, useEffect, useRef } from "react";

type Opts = {
  active: boolean;
  containerRef: RefObject<HTMLElement>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  onEscape?: () => void;
  restoreFocus?: boolean;
};

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, details,[tabindex]:not([tabindex="-1"])',
    ),
  );
  return nodes.filter((el) => {
    const style = window.getComputedStyle(el);
    return style.visibility !== "hidden" && style.display !== "none" && el.offsetParent !== null;
  });
}

export function useFocusTrap({ active, containerRef, initialFocusRef, onEscape, restoreFocus = true }: Opts) {
  const prevFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return;

    prevFocusRef.current = (document.activeElement as HTMLElement) || null;

    const focusTarget =
      (initialFocusRef?.current as HTMLElement | null) || getFocusable(container)[0] || container;

    // Defer to ensure element is mounted
    const tid = window.setTimeout(() => {
      focusTarget?.focus?.();
    }, 0);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && onEscape) {
        e.stopPropagation();
        e.preventDefault();
        onEscape();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement as HTMLElement;

      if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (current === first || !focusables.includes(current))) {
        e.preventDefault();
        last.focus();
      }
    }

    container.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(tid);
      container.removeEventListener("keydown", onKeyDown);
      if (restoreFocus && prevFocusRef.current) {
        try {
          prevFocusRef.current.focus();
        } catch {}
      }
    };
  }, [active, containerRef, initialFocusRef, onEscape, restoreFocus]);
}
